import { useState } from 'react'
import { ClubMember } from '../../generated'

type EditableUserId = NonNullable<ClubMember['userId']>

interface RoleOption {
  id: number
  name: string
  description?: string
}

interface RoleChangePayload {
  userId: EditableUserId
  userName: string
  roleId: number
  roleName: string
}

interface RemovePayload {
  userId: EditableUserId
  userName: string
}

interface ClubMembersTableProps {
  members: ClubMember[]
  roles: RoleOption[]
  onRoleChange: (payload: RoleChangePayload) => void
  onRemove: (payload: RemovePayload) => void
  onResendInvite: (payload: {
    name: string
    email: string
    roleId: number
  }) => void
}

interface ClubMemberRowProps {
  member: ClubMember
  roles: RoleOption[]
  onRoleChange: ClubMembersTableProps['onRoleChange']
  onRemove: ClubMembersTableProps['onRemove']
  onResendInvite: ClubMembersTableProps['onResendInvite']
}

const ONBOARDING_STATUS: Record<
  ClubMember['onboardingStatus'],
  { label: string; className: string }
> = {
  PENDING: { label: 'Pending', className: 'bg-amber-100 text-amber-800' },
  NOT_STARTED: { label: 'Not started', className: 'bg-gray-100 text-gray-600' },
  COMPLETED: { label: 'Completed', className: 'bg-green-100 text-green-700' },
}

const ClubMemberRow = ({
  member,
  roles,
  onRoleChange,
  onRemove,
  onResendInvite,
}: ClubMemberRowProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [selectedRoleId, setSelectedRoleId] = useState(member.roleId)
  const editableUserId: EditableUserId | null = member.userId ?? null
  const canEditRole = editableUserId != null
  const needsInvite = member.onboardingStatus !== 'COMPLETED'
  const displayRoleName =
    roles.find((role) => role.id === member.roleId)?.name ?? 'Unknown role'
  const status = ONBOARDING_STATUS[member.onboardingStatus]

  const toggleEdit = () => {
    setIsEditing((prev) => !prev)
  }

  const handleCancel = () => {
    setSelectedRoleId(member.roleId)
    setIsEditing(false)
  }

  const handleSave = () => {
    if (editableUserId != null && selectedRoleId !== member.roleId) {
      const roleName =
        roles.find((role) => role.id === selectedRoleId)?.name ||
        'the selected role'

      onRoleChange({
        userId: editableUserId,
        userName: member.userName,
        roleId: selectedRoleId,
        roleName,
      })
    }

    setIsEditing(false)
  }

  const handleRemove = () => {
    if (editableUserId == null) {
      return
    }

    onRemove({ userId: editableUserId, userName: member.userName })
    setIsEditing(false)
  }

  const handleResend = () =>
    onResendInvite({
      name: member.userName,
      email: member.userEmail,
      roleId: member.roleId,
    })

  return (
    <tr>
      <td className="w-[33%] pr-3 pl-4 pt-4 text-sm font-medium whitespace-nowrap text-gray-700 sm:pl-0">
        <p>{member.userName}</p>
        <p className="text-gray-400 font-normal h-[20px]">{member.userEmail}</p>
      </td>
      <td className="px-3 text-sm whitespace-nowrap text-gray-500">
        {isEditing && canEditRole ? (
          <select
            value={selectedRoleId}
            onChange={(event) => setSelectedRoleId(Number(event.target.value))}
            className="w-full h-[32px] py-0.5 pr-8 pl-3 appearance-none rounded-md bg-white text-base text-gray-900 focus:outline-1 focus:-outline-offset-1 focus:outline-indigo-500 sm:text-sm/6 border-gray-300 shadow-sm">
            {roles.map(({ id, name }) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        ) : (
          <span>{displayRoleName}</span>
        )}
      </td>
      <td className="px-3 text-sm whitespace-nowrap">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${status.className}`}>
          {status.label}
        </span>
      </td>
      <td className="relative h-[60px] pr-4 pl-3 align-middle text-right text-sm whitespace-nowrap sm:pr-0">
        {isEditing ? (
          <div className="inline-flex items-center justify-end gap-3">
            {needsInvite && (
              <button
                type="button"
                className="text-indigo-600 hover:text-indigo-900"
                onClick={handleResend}>
                Resend invite
              </button>
            )}
            <button
              type="button"
              onClick={handleRemove}
              className="px-4 py-1.5 inline-flex items-center justify-center rounded-md bg-white text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
              Remove
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-1.5 inline-flex items-center justify-center rounded-md bg-white text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
              Cancel
            </button>
            {canEditRole && (
              <button
                type="button"
                onClick={handleSave}
                className="px-4 py-1.5 inline-flex items-center justify-center rounded-md bg-indigo-600 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
                Save
              </button>
            )}
          </div>
        ) : (
          <div className="inline-flex items-center justify-end gap-3">
            {needsInvite && (
              <button
                type="button"
                className="text-indigo-600 hover:text-indigo-900"
                onClick={handleResend}>
                Resend invite
              </button>
            )}
            {canEditRole && (
              <button
                type="button"
                className="text-indigo-600 hover:text-indigo-900"
                onClick={toggleEdit}>
                Edit
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  )
}

const ClubMembersTable = ({
  members,
  roles,
  onRoleChange,
  onRemove,
  onResendInvite,
}: ClubMembersTableProps) => (
  <table className="min-w-full divide-y divide-gray-300">
    <thead>
      <tr>
        <th
          scope="col"
          className="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-700 sm:pl-0">
          Name
        </th>
        <th
          scope="col"
          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-700">
          Access role
        </th>
        <th
          scope="col"
          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-700">
          Onboarding
        </th>
        <th scope="col" className="relative py-3.5 pr-0 pl-3">
          <span className="sr-only">Edit</span>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td colSpan={4} className="h-[10px] p-2" />
      </tr>
      {members.map((member) => (
        <ClubMemberRow
          key={`${member.userId ?? member.userEmail}-${member.roleId}`}
          member={member}
          roles={roles}
          onRoleChange={onRoleChange}
          onRemove={onRemove}
          onResendInvite={onResendInvite}
        />
      ))}
    </tbody>
  </table>
)

export default ClubMembersTable
